import { Routes, useParam } from "@blitzjs/next"
import { useQuery } from "@blitzjs/rpc"
import { SBadge, SButton, SDropdown, SIcon, SIconType } from "@superwall-me/sui"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { ApplicationPlatform } from "@superwall/db/constants"
import ApplicationIcon from "app/applications/components/ApplicationIcon"
import { OnboardingContext } from "app/onboardingV2/OnboardingContext"
import getOrganization from "app/organizations/queries/getOrganization"
import { trpc } from "app/core/trpcClient"
import Link from "next/link"
import { useRouter } from "next/router"
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createRef,
  useContext,
  useEffect,
  useState,
} from "react"
import Loader from "react-spinners/ScaleLoader"
import tw from "tailwind-styled-components"
import { LogoutButton } from "../components/header/LogoutButton"
import { useApplicationContext } from "../contexts/ApplicationContext"
import useIsMobile from "../hooks/useIsMobile"
// import AppSwitcher from "./ProjectSwitcher"
import ProjectSwitcher from "./ProjectSwitcher"
import getCurrentUser from "app/users/queries/getCurrentUser"
import { LocalizationModal } from "../components/LocalizationModal"
import { Application } from "@superwall/db/constants"

const KIconWidth = 17

const SidebarWrapper = tw.div`
  SidebarWrapper
  flex
  flex-col
  items-start
  fixed
  top-0
  left-0
  bottom-0
  z-[800]
  border-r-[1px]
  border-white
  border-opacity-7
  overflow-y-scroll
  h-[100vh]
  w-[72px]
  min-w-[72px]
  transition-all
  ease-in-out
  hover:w-[275px]
  hover:min-w-[275px]
  hover:backdrop-blur-xl
  hover:bg-opacity-50
  lg:w-[275px]
  lg:min-w-[275px]
  hover:lg:bg-grayscale-darker
  bg-grayscale-darker
`

export const ModalContentWrapper = tw.div<{ $disableOverflowStyle?: boolean }>`
  p-6
  pr-3
`

const MobileWrapper = tw.div<{ $isMenuOpen: boolean }>`
  MobileWrapper
  flex
  flex-col
  items-start
  fixed
  top-0
  left-0
  right-0
  z-[950]
  border-b-[1px]
  border-white
  border-opacity-7
  transition-all
  ease-in-out
  bg-grayscale-darker


  ${({ $isMenuOpen }) => {
    if ($isMenuOpen) {
      return `h-[100vh] overflow-y-scroll overscroll-y-contain overflow-x-hidden`
    } else {
      return `h-[64px] overflow-hidden`
    }
  }}
`

const SidebarSpacer = tw.div`
  SidebarSpacer
  h-[64px]
  min-h-[64px]
  sm:w-[72px]
  sm:min-w-[72px]
  lg:w-[275px]
  lg:min-w-[275px]
  lg:relative
`

const LogoSection = tw.div`
  py-5 w-full flex items-center gap-2 justify-center border-b-[1px] border-white border-opacity-7
  h-[64px]
  min-h-[64px]
  sm:h-[72px]
  sm:min-h-[72px]
`

export const AppSwitcherSection = tw.div`
  p-4 w-full flex items-center gap-2 justify-start border-b-[1px] border-white border-opacity-7
  pr-2 gap-4 cursor-pointer min-h-[72px]
`

const LinksSection = tw.div`
relative
  flex flex-col w-full p-5 gap-0 sm:flex-grow sm:overflow-y-auto
  order-last sm:order-none
`

const AccountSection = tw.div`
  p-4 py-5 flex items-center gap-2 justify-start border-b-[1px] sm:border-t-[1px] border-white border-opacity-7
  sm:sticky left-0 bottom-0 w-[calc(100vw-1px)] sm:w-[274px]
  bg-grayscale-darker
  cursor-pointer
  h-[72px]
`

const Anchor = tw.a<{ $isCurrentPath: boolean }>`
  transition-all
  duration-200

  origin-left

  ${({ $isCurrentPath }) =>
    $isCurrentPath
      ? "text-brand-primary text-opacity-100 transform scale-[1.05]"
      : "text-grayscale-lightest  hover:text-white"}

  cursor-pointer
  active:scale-[0.97]
  grid grid-cols-[auto,1fr]
  text-lg
  items-center
  gap-[25px]
  ml-[8px]
  w-[90%]
  sm:w-auto
  lg:w-[90%]
  grow
  py-2
`

export const Loading: FC<{ bottomBorder?: boolean }> = ({ bottomBorder = false }) => {
  return (
    <div
      className={`flex flex-row items-center justify-center text-brand-primary w-full min-h-[73px] ${
        bottomBorder ? "border-b-[1px]" : ""
      } border-white border-opacity-7`}
    >
      <Loader color="rgba(255,255,255,0.1)"></Loader>
    </div>
  )
}

const DyanimcSidebarWrapper: FC<
  PropsWithChildren<{
    applicationId: number
    application: (Partial<Application> & Pick<Application, "name">) | undefined
    setAppSwitcherOpen: Dispatch<SetStateAction<boolean>>
  }>
> = ({ applicationId, application, setAppSwitcherOpen, children }) => {
  const { pathname } = useRouter()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const mobileWrapperRef = createRef<HTMLDivElement>()
  const [currentPathName, setCurrentPathName] = useState("")
  const [currentApplicationId, setCurrentApplicationId] = useState(-1)

  useEffect(() => {
    if (!isMenuOpen) {
      if (mobileWrapperRef.current && mobileWrapperRef.current.scrollTop !== 0) {
        mobileWrapperRef.current.scrollTop = 0
      }
    }
    if (pathname !== currentPathName || applicationId !== currentApplicationId) {
      setCurrentPathName(pathname)
      setCurrentApplicationId(applicationId)

      if (isMenuOpen) {
        setIsMenuOpen(false)
      }
    }
  }, [pathname, currentPathName, isMenuOpen, mobileWrapperRef, applicationId, currentApplicationId])

  const isMobile = useIsMobile()

  return isMobile ? (
    <MobileWrapper ref={mobileWrapperRef} $isMenuOpen={isMenuOpen}>
      <SButton
        isMinimal
        onClick={() => {
          setIsMenuOpen(!isMenuOpen)
        }}
        className="absolute sm:hidden h-[42px] top-[11px] right-[-2px] !pl-7 !pr-5 !py-0"
      >
        {isMenuOpen ? MenuIcons[1] : MenuIcons[0]}
      </SButton>
      <div
        onClick={() => {
          setIsMenuOpen(true)
          setAppSwitcherOpen(true)
        }}
        className={`!transition-all duration-400 absolute sm:hidden h-[42px] left-[-4px] !pl-5 !pr-7 !py-0 ${
          isMenuOpen ? "top-[51px] opacity-0" : "top-[14px] opacity-100"
        }`}
      >
        {application && <ApplicationIcon size={34} application={application!} />}
      </div>
      {children}
    </MobileWrapper>
  ) : (
    <SidebarWrapper>{children}</SidebarWrapper>
  )
}

const Sidebar: FC<
  PropsWithChildren<{
    title?: string
    subtitle?: string
  }>
> = ({ title, subtitle, children }) => {
  const applicationId = useParam("applicationId", "number")!
  const { data: application, isLoading } = trpc.blitzMigration.applications.getApplication.useQuery(
    { id: applicationId },
    { suspense: false }
  )
  const [organization] = useQuery(getOrganization, { applicationId }, { suspense: false })
  const [user, userRequest] = useQuery(getCurrentUser, null, {
    suspense: false,
  })
  const { enabled } = useApplicationContext()
  const { pathname } = useRouter()
  const [isHoveringSidebar, setIsHoveringSidebar] = useState(false)
  const [appSwitcherOpen, setAppSwitcherOpen] = useState(false)
  const [appSwitcherTimeout, setAppSwitcherTimeout] = useState(setTimeout(() => {}, 0))
  const [localizationModalOpen, setLocalizationModalOpen] = useState(false)
  const isMobile = useIsMobile()
  const onboardingContext = useContext(OnboardingContext)
  const wrappedEnabled = useFeatureFlagEnabled("superwall_wrapped_2025")

  const router = useRouter()

  useEffect(() => {
    if (isMobile) {
      setIsHoveringSidebar(false)
    }

    if (!isLoading && !application && applicationId !== undefined) {
      router.push(Routes.SelectOrganization())
    }
  }, [isMobile, isLoading, application, router, applicationId])

  const links = [
    ...(application?.platform != ApplicationPlatform.STRIPE
      ? [
          {
            linkName: "Quickstart",
            href: Routes.ShowGettingStartedPage({ applicationId }),
            pathnames: ["quickstart"],
            hide:
              (applicationId === undefined ||
                onboardingContext.onboardingCompleted ||
                !onboardingContext.isReady) &&
              onboardingContext.transactionCompleted, // show in the sidebar if they have revenue tracking (i.e. onbaording is completed) but no SDK setup
            badge: "",
            icon: QuickstartIcon,
          },
        ]
      : []),
    {
      linkName: "Overview",
      href:
        application?.platform === ApplicationPlatform.WEB
          ? Routes.ShowWebApplicationDashboardPage({ applicationId })
          : Routes.ShowApplicationDashboardPage({ applicationId }),
      pathnames: ["[applicationId]"],
      hide:
        applicationId === undefined ||
        (!onboardingContext.onboardingCompleted &&
          application?.platform !== ApplicationPlatform.STRIPE),
      badge: "",
      icon: OverviewIcon,
    },
    {
      linkName: "Paywalls",
      href: Routes.PaywallsPage({ applicationId }),
      pathnames: ["paywalls", "paywalls/archived"],
      hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
      badge: "",
      icon: PaywallsIcon,
    },
    {
      linkName: "Templates",
      href: Routes.TemplatesPage({ applicationId }),
      pathnames: ["templates"],
      hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
      badge: "",
      icon: TemplatesIcon,
    },
    {
      linkName: "Campaigns",
      href: Routes.ExperimentGroupPage({ applicationId }),
      pathnames: ["rules", "rules/[experimentGroupId]", "rules/archived"],
      hide: applicationId === undefined,
      badge: "",
      icon: CampaignsIcon,
    },
    {
      linkName: "Charts",
      href: Routes.ShowChartsV2Page({ applicationId }),
      pathnames: ["charts"],
      hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
      badge: "",
      icon: ChartsIcon,
    },

    ...(enabled("rule_entitlements")
      ? [
          {
            linkName: "Products",
            href: Routes.ApplicationProductsV2({ applicationId }),
            pathnames: ["products", "products/v2"],
            hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
            badge: "",
            icon: ProductsIcon,
          },
        ]
      : [
          {
            linkName: "Products",
            href: Routes.ApplicationProducts({ applicationId }),
            pathnames: ["products", "products/v2"],
            hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
            badge: "",
            icon: ProductsIcon,
          },
        ]),
    ...(enabled("surveys") && application?.platform !== ApplicationPlatform.STRIPE
      ? [
          {
            linkName: "Surveys",
            href: Routes.ApplicationSurveysPage({ applicationId }),
            pathnames: ["surveys"],
            hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
            badge: "",
            icon: SurveysIcon,
          },
        ]
      : []),
    ...(enabled("user_page_v2")
      ? [
          {
            linkName: "Users",
            href: Routes.UsersPageV2({ applicationId }),
            pathnames: ["users/v2", "users/v2/[appUserId]", "events/[eventId]"],
            hide: applicationId === undefined,
            badge: "",
            icon: UsersIcon,
          },
        ]
      : [
          {
            linkName: "Users",
            href: Routes.UsersPage({ applicationId }),
            pathnames: ["users", "users/[appUserId]"],
            hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
            badge: "",
            icon: UsersIcon,
          },
        ]),
    ...(application?.localizationProviderId != null
      ? [
          // {
          //   linkName: "Localization (v3)",
          //   href: Routes.ApplicationLocalizationPage({
          //     applicationId: applicationId,
          //   }),
          //   pathnames: ["localization"],
          //   hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
          //   badge: "",
          //   icon: LocalizationIcon,
          // },
        ]
      : []),
    // {
    //   linkName: "Localization",
    //   href: Routes.ApplicationLocalizationPage({
    //     applicationId: applicationId,
    //   }),
    //   pathnames: ["localization-legacy/[locale]", "localization-legacy", "localization"],
    //   hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
    //   badge: "",
    //   icon: LocalizationIcon,
    // },
    ...(enabled("etl_v2")
      ? [
          {
            linkName: "ETL",
            href: Routes.ApplicationETLConfigurationPage({ applicationId }),
            pathnames: ["etl"],
            badge: "",
            hide: applicationId === undefined || application?.platform === ApplicationPlatform.WEB,
            icon: NotificationsIcon,
          },
        ]
      : []),
    {
      linkName: "Teams",
      href: Routes.SelectOrganization(),
      pathnames: ["select-organization"],
      badge: "",
      hide: applicationId !== undefined,
      icon: SelectOrganizationIcon,
    },
    {
      linkName: "Docs",
      href: "https://docs.superwall.com",
      external: true,
      pathnames: ["docs"],
      badge: "",
      icon: DocsIcon,
    },
    {
      linkName: "Blog",
      href: "/blog",
      external: true,
      pathnames: ["blog"],
      badge: "",
      icon: BlogIcon,
    },
    ...(enabled("svix_webhooks")
      ? [
          {
            linkName: "Integrations",
            href: `/applications/${applicationId}/integrations`,
            pathnames: [
              "integrations",
              "integrations/[integrationId]",
              "integrations/asa",
              "webhooks",
              "webhooks/[id]",
              "webhooks/create",
              "!settings/integrations",
            ],
            hide: applicationId === undefined,
            badge: "",
            icon: IntegrationsIcon,
          },
        ]
      : []),
    ...(wrappedEnabled
      ? [
          {
            linkName: "Wrapped",
            href: `/applications/${applicationId}/wrapped`,
            pathnames: ["wrapped"],
            badge: "2025",
            hide: applicationId === undefined,
            icon: WrappedIcon,
          },
        ]
      : []),
    {
      linkName: "Settings",
      href: Routes.ApplicationSettingsPage({ applicationId }),
      pathnames: ["settings"],
      badge: "",
      hide: applicationId === undefined,
      icon: SettingsIcon,
    },
    {
      linkName: "Admin",
      href: Routes.AdminApplication({ applicationId }),
      pathnames: ["admin"],
      badge: "",
      hide: applicationId === undefined || user?.role !== "SUPERADMIN",
      icon: AdminIcon,
    },
    {
      linkName: "Admin",
      href: Routes.AdminHome(),
      pathnames: ["admin"],
      badge: "",
      hide: applicationId !== undefined || user?.role !== "SUPERADMIN",
      icon: AdminIcon,
    },
  ]

  const isActiveLink = (l: (typeof links)[0]) => {
    if (l.hide) return false

    const negativeMatches = l.pathnames.filter((p) => p.startsWith("!"))
    for (const negPath of negativeMatches) {
      const pathWithoutExclamation = negPath.slice(1)
      if (
        pathname.endsWith(pathWithoutExclamation) ||
        (pathWithoutExclamation === "settings" &&
          pathname.startsWith("/applications/[applicationId]/settings")) ||
        (pathWithoutExclamation === "admin" && pathname.startsWith("/admin"))
      ) {
        return false
      }
    }

    return l.pathnames.some(
      (p) =>
        !p.startsWith("!") &&
        ((pathname.endsWith(p) && !pathname.startsWith("/admin")) ||
          (p == "settings" && pathname.startsWith("/applications/[applicationId]/settings")) ||
          (p == "admin" && pathname.startsWith("/admin")))
    )
  }

  return (
    <div
      onMouseLeave={() => {
        const timeout = setTimeout(() => {
          setAppSwitcherOpen(false)
        }, 500)
        setAppSwitcherTimeout(timeout)
        setIsHoveringSidebar(false)
      }}
      onMouseEnter={() => {
        setIsHoveringSidebar(true)
        clearTimeout(appSwitcherTimeout)
      }}
    >
      <DyanimcSidebarWrapper
        applicationId={applicationId}
        application={application}
        setAppSwitcherOpen={setAppSwitcherOpen}
      >
        <LogoSection>
          {isMobile ? (
            <a className="flex items-center justify-center" href="/">
              {LogoLarge}
            </a>
          ) : (
            <a className="lg:hidden relative flex items-center justify-center" href="/">
              <div
                style={{ transform: isHoveringSidebar ? "scale(1)" : "scale(0.1)" }}
                className={`absolute transition-all ease-in-out duration-100 opacity-${
                  isHoveringSidebar ? 100 : 0
                }`}
              >
                {LogoLarge}
              </div>
              <div
                style={{ transform: isHoveringSidebar ? "scale(0.1)" : "scale(1)" }}
                className={`absolute transition-all ease-in-out duration-200 opacity-${
                  isHoveringSidebar ? 0 : 100
                }`}
              >
                {LogoSmall}
              </div>
            </a>
          )}

          {!isMobile && (
            <a className="hidden lg:flex items-center justify-center" href="/">
              {LogoLarge}
            </a>
          )}
        </LogoSection>
        {/* {application ? (
          <AppSwitcherSection
            onClick={() => {
              setAppSwitcherOpen(!appSwitcherOpen)
            }}
          >
            <span className="opacity-40 ml-auto absolute z-0 right-2">
              {appSwitcherOpen ? CloseAppSwitcherIcon : OpenAppSwitcherIcon}
            </span>
            <ApplicationIcon application={application} showPlatform={true} />
            <div className="pl-2">
              <h2 className="w-[165px] truncate">{application.name}</h2>
              <h2 className="text-xs opacity-50 w-[175px] truncate">{organization?.name}</h2>
            </div>
          </AppSwitcherSection>
        ) : (
          applicationId && <Loading bottomBorder={true}></Loading>
        )}
        <AppSwitcher open={appSwitcherOpen} setOpen={setAppSwitcherOpen}></AppSwitcher> */}
        <ProjectSwitcher isHovered={isHoveringSidebar} />
        <LinksSection>
          {links.map((l, i) => {
            if (l.hide) return

            return (
              <span id={`sidebar-${l.linkName.toLowerCase()}`} key={`side-menu-` + l.linkName}>
                {l.external ? (
                  <Anchor
                    href={l.href as any}
                    target="_blank"
                    rel="noreferrer"
                    $isCurrentPath={false}
                  >
                    {l.icon ? l.icon : null}
                    {l.linkName}{" "}
                    {l.badge ? <SBadge className="scale-[0.9]">{l.badge}</SBadge> : null}
                  </Anchor>
                ) : l.linkName === "Localization" ? (
                  <Anchor
                    $isCurrentPath={false}
                    onClick={(e) => {
                      e.preventDefault()
                      setLocalizationModalOpen(true)
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {l.icon ? l.icon[0] : null}
                    {l.linkName}{" "}
                    {l.badge ? <SBadge className="scale-[0.9]">{l.badge}</SBadge> : null}
                  </Anchor>
                ) : (
                  <Link passHref href={l.href as any} legacyBehavior>
                    <Anchor $isCurrentPath={isActiveLink(l)}>
                      {l.icon ? (isActiveLink(l) ? l.icon[1] : l.icon[0]) : null}
                      <span className="flex items-center gap-2">
                        {l.linkName}
                        {l.badge ? (
                          l.badge.toLowerCase() == "new" ? (
                            <SBadge.New endDate="2024-12-01"></SBadge.New>
                          ) : (
                            <SBadge className="scale-[0.9]">{l.badge}</SBadge>
                          )
                        ) : null}
                      </span>
                    </Anchor>
                  </Link>
                )}
              </span>
            )
          })}

          {isMobile ? <CalloutButtons isHoveringSidebar={false} /> : null}
        </LinksSection>

        {/* Container for bottom sticky elements */}
        <div className="sm:mt-auto sm:w-[274px]">
          {/* Callout Buttons */}
          {!isMobile ? <CalloutButtons isHoveringSidebar={isHoveringSidebar} /> : null}
          {/* Account Section */}
          <AccountSection>
            {user ? (
              <div className="w-full flex items-center justify-center">
                <SDropdown
                  style={{ width: "100%" }}
                  className="bottom-[71px] left-[-4px] flex text-left "
                  width={250}
                  isUnstyledItems
                  elements={[
                    <a key={`manage`} href={`${process.env.NEXT_PUBLIC_AUTH_URL}/manage`}>
                      <SDropdown.Item>{`Manage`}</SDropdown.Item>
                    </a>,
                    <Link key={`switch`} href={Routes.SelectOrganization()} legacyBehavior>
                      <SDropdown.Item>{`Switch Teams`}</SDropdown.Item>
                    </Link>,
                    <SDropdown.Item key={`logout`}>
                      <LogoutButton />
                    </SDropdown.Item>,
                  ]}
                >
                  <div className="flex items-center gap-2 text-[rgba(255,255,255,0.4)] ml-1 hover:text-brand-primary">
                    {/* <ApplicationIcon application={application} /> */}
                    <span className="z-0 absolute right-[3px]">
                      <div className={`py-1 transition-all `}>{LogOutIcon}</div>
                    </span>
                    <div className="relative ml-[-1px] z-100 text-brand-primary mr-2 rounded-full border border-brand-primary border-opacity-20 bg-[#202B2E] min-w-[34px] min-h-[34px] w-[34px] h-[34px] flex items-end justify-center overflow-hidden">
                      {ProfileIcon}
                    </div>
                    <div className="flex ml-[2px] flex-col items-start justify-start text-left text-white">
                      <h2 className="text-sm w-[165px] truncate">{user.name}</h2>
                      <h2 className="text-xs w-[165px] truncate opacity-50">{user.email}</h2>
                    </div>
                  </div>
                </SDropdown>
              </div>
            ) : (
              userRequest.isLoading && <Loading></Loading>
            )}
          </AccountSection>
        </div>
      </DyanimcSidebarWrapper>
      <SidebarSpacer />
      <LocalizationModal
        isOpen={localizationModalOpen}
        onClose={() => setLocalizationModalOpen(false)}
      />
    </div>
  )
}

export const SidebarPlaceholder = () => (
  <div>
    <SidebarWrapper>
      {/* Logo Section */}
      <LogoSection>
        <a className="hidden lg:flex items-center justify-center" href="/">
          {LogoLarge}
        </a>
        <a className="lg:hidden relative flex items-center justify-center" href="/">
          <div className="absolute">{LogoSmall}</div>
        </a>
      </LogoSection>

      {/* Links Section */}
      <LinksSection>
        {/* Quickstart */}
        <span>
          <Anchor href="/quickstart" $isCurrentPath={false}>
            {QuickstartIcon[0]}
            Quickstart
          </Anchor>
        </span>

        {/* Teams */}
        <span>
          <Anchor href="/select-organization" $isCurrentPath={false}>
            {SelectOrganizationIcon[0]}
            Teams
          </Anchor>
        </span>

        {/* Docs */}
        <span>
          <Anchor
            href="https://docs.superwall.com"
            target="_blank"
            rel="noreferrer"
            $isCurrentPath={false}
          >
            {DocsIcon}
            Docs
          </Anchor>
        </span>

        {/* Blog */}
        <span>
          <Anchor href="/blog" target="_blank" rel="noreferrer" $isCurrentPath={false}>
            {BlogIcon}
            Blog
          </Anchor>
        </span>
      </LinksSection>

      {/* Container for bottom sticky elements */}
      <div className="sm:mt-auto sm:w-[274px]">
        {/* Account Section */}
        <AccountSection>
          <Loading />
        </AccountSection>
      </div>
    </SidebarWrapper>
    <SidebarSpacer />
  </div>
)

export const JoinDiscordButton = () => {
  const { data } = trpc.blitzMigration.discord.getDiscordInfo.useQuery({}, { suspense: false })

  if (!data) return null
  return (
    <SButton
      href={data.instant_invite}
      className="!p-0 !m-0"
      target="_blank"
      variant="secondary"
      isMinimal
    >
      <div className="flex items-center bg-grayscale-darker justify-between gap-2 p-2.5 border border-white/10 rounded-xl w-full !font-normal">
        <div className="flex items-center gap-3">
          <div className="w-[36px] h-[36px] bg-[#5765F2] text-white rounded-lg flex items-center justify-center">
            <SIcon type={SIconType.Discord} />
          </div>
          <div>
            <p>Join Our Discord</p>
            <div className="flex items-center gap-1">
              <div className="w-[8px] h-[8px] bg-green-400 rounded-full"></div>
              <p className="text-xs opacity-50">{data.presence_count} online</p>
            </div>
          </div>
        </div>
        <p className="pr-2 opacity-50 text-xs">Join</p>
      </div>
    </SButton>
  )
}

const CalloutButtons = ({ isHoveringSidebar }: { isHoveringSidebar: boolean }) => {
  const { enabled } = useApplicationContext()

  return (
    <div
      className={`
       my-[15px] sm:my-0 lg:p-4 flex flex-col gap-2 opacity-100 lg:opacity-100 lg:h-auto lg:pt-2 lg:overflow-visible
      ${
        isHoveringSidebar
          ? "sm:pt-2 sm:p-4 h-auto overflow-visible sm:opacity-100" // hovered
          : "sm:h-0 sm:overflow-hidden sm:px-4 pb-20 sm:pb-0" // collapsed
      }
      transition-all duration-300`}
    >
      {enabled("affiliate_program_callout") && <JoinReferralButton />}
      <JoinDiscordButton />
    </div>
  )
}

export const JoinReferralButton = () => {
  return (
    <SButton
      href={"https://75v4gfh4e3l.typeform.com/to/qU4x4af2"}
      className="!p-0 !m-0"
      target="_blank"
      variant="secondary"
      isMinimal
    >
      <div className="flex items-center bg-grayscale-darker justify-between gap-2 p-2.5 border border-white/10 rounded-xl w-full !font-normal">
        <div className="flex items-center gap-3">
          <div className="w-[36px] h-[36px] bg-grayscale-dark border border-white/5 text-white rounded-lg flex items-center justify-center">
            <SIcon type={SIconType.Gift} size={22} />
          </div>
          <div>
            <p>Affiliate Program</p>
            <div className="flex items-center gap-1">
              <p className="text-xs opacity-50">Earn up to $500</p>
            </div>
          </div>
        </div>
        <p className="pr-2 opacity-50 text-xs">Refer</p>
      </div>
    </SButton>
  )
}

const CloseAppSwitcherIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <title>caret-sm-up</title>
    <g fill="currentColor">
      <path
        d="M16.707,13.293l-4-4a1,1,0,0,0-1.414,0l-4,4a1,1,0,1,0,1.414,1.414L12,11.414l3.293,3.293a1,1,0,0,0,1.414-1.414Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>
)

const OpenAppSwitcherIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>menu-5</title>
    <g fill="currentColor">
      <circle fill="currentColor" cx="12" cy="12" r="2"></circle>{" "}
      <circle fill="currentColor" cx="12" cy="3" r="2"></circle>{" "}
      <circle fill="currentColor" cx="12" cy="21" r="2"></circle>
    </g>
  </svg>
)

const LogOutIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>log-out</title>
    <g fill="currentColor">
      <path
        data-color="color-2"
        d="M9.458,17.89A1,1,0,0,0,10,17V13h7a1,1,0,0,0,0-2H10V7a1,1,0,0,0-1.581-.813l-7,5a1,1,0,0,0,0,1.626l7,5A.988.988,0,0,0,9,18,1.019,1.019,0,0,0,9.458,17.89Z"
      ></path>
      <path
        d="M20,0H10a1,1,0,0,0,0,2H20a1,1,0,0,1,1,1V21a1,1,0,0,1-1,1H10a1,1,0,0,0,0,2H20a3,3,0,0,0,3-3V3A3,3,0,0,0,20,0Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>
)

const ProfileIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <title>account</title>
    <g fill="currentColor">
      <circle fill="currentColor" cx="12" cy="7" r="7"></circle>{" "}
      <path
        data-color="color-2"
        d="M18.333,13.387C16.705,15,14.468,16,12,16s-4.705-1-6.333-2.613C2.291,15.267,0,18.869,0,23 v1h24v-1C24,18.869,21.709,15.267,18.333,13.387z"
      ></path>
    </g>
  </svg>
)

const OverviewIcon = [
  <svg
    key="asdfasd22"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>dashboard</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <line
        data-color="color-2"
        fill="none"
        strokeMiterlimit="10"
        x1="1.4"
        y1="15"
        x2="22.6"
        y2="15"
        strokeLinecap="butt"
      ></line>{" "}
      <line
        data-color="color-2"
        fill="none"
        strokeMiterlimit="10"
        x1="7"
        y1="6"
        x2="12"
        y2="15"
        strokeLinecap="butt"
      ></line>{" "}
      <circle
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        cx="12"
        cy="12"
        r="11"
      ></circle>
    </g>
  </svg>,
  <svg
    key="2345"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>dashboard</title>
    <g fill="currentColor">
      <path
        d="M12,0C5.383,0,0,5.383,0,12s5.383,12,12,12,12-5.383,12-12S18.617,0,12,0Zm3.862,14c.184,.705,.182,1.446-.034,2.161-.311,1.023-1,1.863-1.942,2.367-.575,.308-1.227,.472-1.882,.472-1.481,0-2.835-.811-3.531-2.115-.086-.162-.272-.516-1.05-2.885H2.202c-.132-.646-.202-1.315-.202-2C2,6.486,6.486,2,12,2s10,4.486,10,10c0,.685-.07,1.354-.202,2h-5.936Z"
        fill="currentColor"
      ></path>
      <path
        data-color="color-2"
        d="M8.388,7.201c-.151-.186-.412-.239-.623-.125-.211,.113-.312,.359-.243,.587,.236,.769,2.312,7.528,2.715,8.28,.359,.673,1.052,1.057,1.767,1.057,.318,0,.641-.076,.939-.236,.974-.52,1.342-1.734,.822-2.707-.402-.751-4.869-6.234-5.377-6.856Z"
      ></path>
    </g>
  </svg>,
]

const ChartsIcon = [
  <svg
    key="alksdhjfldaksdf"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>edit-curves</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <path d="M5,17C17,17,11,3,23,3" fill="none" strokeMiterlimit="10" data-color="color-2"></path>
      <polyline
        points="23 21 1 21 1 3"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></polyline>
    </g>
  </svg>,
  <svg
    key="alksdhjflaksdf"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>edit-curves</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <path d="M5,17C17,17,11,3,23,3" fill="none" strokeMiterlimit="10" data-color="color-2"></path>
      <polyline
        points="23 21 1 21 1 3"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></polyline>
    </g>
  </svg>,
]

export const PaywallsIcon = [
  <svg
    key="0987aksjdh"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>phone-toolbar</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <path
        d="M18,23H6a2,2,0,0,1-2-2V3A2,2,0,0,1,6,1H18a2,2,0,0,1,2,2V21A2,2,0,0,1,18,23Z"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>
      <line
        data-color="color-2"
        x1="7"
        y1="19"
        x2="17"
        y2="19"
        fill="none"
        strokeMiterlimit="10"
      ></line>
    </g>
  </svg>,

  <svg
    key="2348907a"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>phone-toolbar</title>
    <g fill="currentColor">
      <path
        fill="currentColor"
        d="M18,0H6C4.346,0,3,1.346,3,3v18c0,1.654,1.346,3,3,3h12c1.654,0,3-1.346,3-3V3C21,1.346,19.654,0,18,0z M19,20c0,0.552-0.448,1-1,1H6c-0.552,0-1-0.448-1-1V4c0-0.552,0.448-1,1-1h12c0.552,0,1,0.448,1,1V20z"
      ></path>{" "}
      <rect data-color="color-2" x="7" y="15" width="10" height="4"></rect>
    </g>
  </svg>,
]

const CampaignsIcon = [
  <svg
    key="CampaignsIcon1"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>split</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <line
        x1="1"
        y1="17"
        x2="22"
        y2="17"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="butt"
      ></line>
      <path
        data-color="color-2"
        d="M18,7H10.236A2,2,0,0,0,8.447,8.106L5.5,14"
        fill="none"
        strokeMiterlimit="10"
        strokeLinecap="butt"
      ></path>
      <polyline
        points="18 13 22 17 18 21"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></polyline>
      <polyline
        data-color="color-2"
        points="14 3 18 7 14 11"
        fill="none"
        strokeMiterlimit="10"
      ></polyline>
    </g>
  </svg>,
  <svg
    key="asdfasdf"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>split</title>
    <g fill="currentColor">
      <path
        data-color="color-2"
        d="M5,14a1,1,0,0,0,.833-.445L9.535,8H13v3a1,1,0,0,0,1.625.781l5-4a1,1,0,0,0,0-1.562l-5-4A1,1,0,0,0,13,3V6H9a1,1,0,0,0-.832.445l-4,6A1,1,0,0,0,5,14Z"
      ></path>
      <path
        d="M18.625,12.219A1,1,0,0,0,17,13v3H1a1,1,0,0,0,0,2H17v3a1,1,0,0,0,1.625.781l5-4a1,1,0,0,0,0-1.562Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>,
]

const ProductsIcon = [
  <svg
    key="p1"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>tag</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <path
        d="M14.242,21.414l7.172-7.172a2,2,0,0,0,0-2.829L12,2H2V12l9.413,9.415A2,2,0,0,0,14.242,21.414Z"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>
      <circle data-color="color-2" cx="8" cy="8" r="2" fill="none" strokeMiterlimit="10"></circle>
    </g>
  </svg>,
  <svg
    key="p2"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>tag</title>
    <g fill="currentColor">
      <path
        d="M22.121,10.706,12.706,1.293A1,1,0,0,0,12,1H2A1,1,0,0,0,1,2V12a1,1,0,0,0,.293.707l9.413,9.415a3,3,0,0,0,4.243,0l7.172-7.172a3,3,0,0,0,0-4.243ZM8,10a2,2,0,1,1,2-2A2,2,0,0,1,8,10Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>,
]

const IntegrationsIcon = [
  <svg
    key="asdfasdf"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <path
      d="M11.414 11L9.41402 13L11 14.5859L13 12.5859L14.414 14L12.414 16L13.7421 17.3271C13.9294 17.5144 14.0349 17.7693 14.0351 18.0341C14.0351 18.2993 13.9296 18.5536 13.7421 18.7412L12.2421 20.2412C11.2405 21.2446 9.91575 21.8603 8.50289 21.9785L8.23824 21.9951C6.91508 22.0468 5.60849 21.6584 4.52633 20.8867L1.70699 23.707L1.63375 23.7744C1.55812 23.8385 1.47323 23.8913 1.3818 23.9306C1.25992 23.9829 1.12868 24.0105 0.996056 24.0117L0.896446 24.0078C0.830786 24.0018 0.765311 23.9895 0.70211 23.9707L0.60836 23.9375C0.516471 23.8998 0.430044 23.8488 0.353477 23.7861L0.280235 23.7187C0.209992 23.6484 0.149874 23.5679 0.103477 23.4804L0.0614853 23.3906C0.0113557 23.2678 -0.0138855 23.1355 -0.0127335 23.0029C-0.011489 22.8704 0.0160179 22.7389 0.0683212 22.6171C0.107598 22.5258 0.16049 22.4408 0.224571 22.3652L0.292931 22.2929L3.11227 19.4726C2.34089 18.3907 1.95222 17.0845 2.00387 15.7617L2.02047 15.497C2.1311 14.1728 2.67877 12.9252 3.5732 11.9492L3.7568 11.7578L5.2568 10.2578C5.44422 10.0704 5.69883 9.96492 5.96383 9.96481C6.22868 9.96488 6.48344 10.0706 6.67086 10.2578L7.99996 11.5859L9.99996 9.5859L11.414 11ZM5.17086 13.1718L5.16988 13.1728C4.502 13.8398 4.09233 14.7224 4.01363 15.663C3.93511 16.6038 4.1932 17.5428 4.74117 18.3115L5.13473 18.8632L5.68746 19.2578C6.45614 19.806 7.39506 20.0638 8.3359 19.9853C9.27687 19.9067 10.16 19.4973 10.8271 18.8291L10.8281 18.8271L11.6201 18.0341L11 17.414L9.58492 16L6.58492 13V12.999L5.96383 12.3779L5.17086 13.1718ZM23.0029 -0.012733C23.1355 -0.0138851 23.2678 0.0113561 23.3906 0.0614858C23.5133 0.111673 23.6249 0.18659 23.7187 0.280236C23.8124 0.373947 23.8872 0.485746 23.9375 0.608361C23.9877 0.731121 24.0128 0.863426 24.0117 0.996056L24.0058 1.09469C23.9952 1.19335 23.9699 1.29027 23.9306 1.3818C23.8782 1.5038 23.8025 1.61475 23.707 1.70699L20.8867 4.52633C21.7097 5.68034 22.0962 7.08943 21.9785 8.50192C21.8606 9.91459 21.2452 11.2394 20.2421 12.2412L18.7421 13.7412C18.5546 13.9286 18.3003 14.0341 18.0351 14.0341C17.7703 14.0339 17.5154 13.9284 17.3281 13.7412L10.2568 6.67184C10.0697 6.48441 9.9639 6.22967 9.96383 5.96481C9.96395 5.6998 10.0694 5.4452 10.2568 5.25777L11.7568 3.75777C12.6957 2.81717 13.9191 2.2175 15.2324 2.04879L15.4961 2.02047C16.8203 1.90977 18.142 2.24287 19.2529 2.96285L19.4726 3.11227L22.2929 0.292931C22.3851 0.19748 22.4952 0.120727 22.6171 0.0683217C22.7389 0.0160182 22.8704 -0.0114887 23.0029 -0.012733ZM18.3115 4.74117C17.5427 4.19313 16.6039 3.93502 15.663 4.01363C14.7221 4.09238 13.8389 4.5026 13.1718 5.17086L13.1709 5.17184L12.3779 5.96383L18.0341 11.6201L18.8281 10.8271L18.8291 10.8261C19.4971 10.159 19.9067 9.27673 19.9853 8.3359C20.0637 7.39512 19.8059 6.45609 19.2578 5.68746L18.8632 5.13473L18.3115 4.74117Z"
      fill="currentColor"
    />
  </svg>,
  <svg
    key="asdfasdf"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <g fill="currentColor">
      <path
        d="M12.414,16l2-2L13,12.586l-2,2L9.414,13l2-2L10,9.586l-2,2L6.671,10.258a1,1,0,0,0-1.414,0l-1.5,1.5a5.989,5.989,0,0,0-.644,7.715l-2.82,2.82a1,1,0,1,0,1.414,1.414l2.82-2.82a5.989,5.989,0,0,0,7.716-.645l1.5-1.5a1,1,0,0,0,0-1.414Z"
        fill="currentColor"
      ></path>
      <path
        d="M17.329,13.742a1,1,0,0,0,1.414,0l1.5-1.5a5.989,5.989,0,0,0,.644-7.715l2.82-2.82A1,1,0,1,0,22.293.293l-2.82,2.82a5.989,5.989,0,0,0-7.716.645l-1.5,1.5a1,1,0,0,0,0,1.414Z"
        data-color="color-2"
      ></path>
    </g>
  </svg>,
]

const UsersIcon = [
  <svg
    key="asd22f"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>users-wm</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <path
        d="M19,21h4V16.677a1,1,0,0,0-.629-.928l-3.742-1.5A1,1,0,0,1,18,13.323v-.878A3.982,3.982,0,0,0,20,9V7a4,4,0,0,0-6-3.465"
        fill="none"
        strokeMiterlimit="10"
        data-color="color-2"
      ></path>{" "}
      <path
        d="M14.371,16.749l-3.742-1.5A1,1,0,0,1,10,14.323v-.451C13.27,13.435,14,12,14,12s-2-2-2-4A4,4,0,0,0,4,8c0,2-2,4-2,4s.73,1.435,4,1.872v.451a1,1,0,0,1-.629.928l-3.742,1.5A1,1,0,0,0,1,17.677V21H15V17.677A1,1,0,0,0,14.371,16.749Z"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>
    </g>
  </svg>,
  <svg
    key={"asdf221"}
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>users-wm</title>
    <g fill="currentColor">
      <path
        d="M23.371,14.749l-3.742-1.5A1,1,0,0,1,19,12.323v-.878A3.982,3.982,0,0,0,21,8V6.172a4.116,4.116,0,0,0-3.607-4.153A4,4,0,0,0,13,6V8a3.982,3.982,0,0,0,2,3.445v.878a4.737,4.737,0,0,1-1.6,2.065c-.068.044-.134.093-.2.138l.911.365A2.988,2.988,0,0,1,16,17.677V20a2.966,2.966,0,0,1-.184,1H23a1,1,0,0,0,1-1V15.677A1,1,0,0,0,23.371,14.749Z"
        data-color="color-2"
      ></path>{" "}
      <path
        d="M13.371,16.749l-3.742-1.5A1,1,0,0,1,9,14.323v-.451a7.18,7.18,0,0,0,3.248-1.115.988.988,0,0,0,.181-1.416A5.991,5.991,0,0,1,11,8.038a3.879,3.879,0,0,0-1.9-3.5A4.046,4.046,0,0,0,3,8a5.943,5.943,0,0,1-1.429,3.342.987.987,0,0,0,.181,1.415A7.18,7.18,0,0,0,5,13.872v.451a1,1,0,0,1-.629.928l-3.742,1.5A1,1,0,0,0,0,17.677V20a1,1,0,0,0,1,1H13a1,1,0,0,0,1-1V17.677A1,1,0,0,0,13.371,16.749Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>,
]

const LocalizationIcon = [
  <svg
    key="2341234"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>ic_translate_24px</title>
    <g fill="currentColor">
      <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"></path>
    </g>
  </svg>,
  <svg
    key="2341s234"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>ic_translate_24px</title>
    <g fill="currentColor">
      <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"></path>
    </g>
  </svg>,
]

const NotificationsIcon = [
  <svg
    key="asdfa234aaa"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>new-notification</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <path
        d="M19,14v6c0,1.105-.895,2-2,2H4c-1.105,0-2-.895-2-2V7c0-1.105,.895-2,2-2h6"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>
      <circle data-color="color-2" cx="19" cy="5" r="4" fill="none" strokeMiterlimit="10"></circle>
    </g>
  </svg>,
  <svg
    key="asdfssa234aaa"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>new-notification</title>
    <g fill="currentColor">
      <path
        d="M19,13c-.552,0-1,.447-1,1v6c0,.552-.449,1-1,1H4c-.551,0-1-.448-1-1V7c0-.552,.449-1,1-1h6c.552,0,1-.447,1-1s-.448-1-1-1H4c-1.654,0-3,1.346-3,3v13c0,1.654,1.346,3,3,3h13c1.654,0,3-1.346,3-3v-6c0-.553-.448-1-1-1Z"
        fill="currentColor"
      ></path>
      <circle data-color="color-2" cx="19" cy="5" r="5"></circle>
    </g>
  </svg>,
]

const DocsIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
    <title>book</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <line
        data-color="color-2"
        fill="none"
        strokeMiterlimit="10"
        x1="12"
        y1="4"
        x2="12"
        y2="21"
        strokeLinecap="butt"
      ></line>{" "}
      <path
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        d="M17.5,2c-3,0-5.5,1.3-5.5,3 c0-1.7-2.5-3-5.5-3S1,3.3,1,5v17c0-1.7,2.5-3,5.5-3s5.5,1.3,5.5,3c0-1.7,2.5-3,5.5-3s5.5,1.3,5.5,3V5C23,3.3,20.5,2,17.5,2z"
      ></path>
    </g>
  </svg>
)

const BlogIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
    <title>news</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <line
        data-color="color-2"
        x1="9"
        y1="14"
        x2="19"
        y2="14"
        fill="none"
        strokeMiterlimit="10"
      ></line>{" "}
      <line
        data-color="color-2"
        x1="9"
        y1="18"
        x2="19"
        y2="18"
        fill="none"
        strokeMiterlimit="10"
      ></line>{" "}
      <rect
        data-color="color-2"
        x="9"
        y="6"
        width="10"
        height="4"
        fill="none"
        strokeMiterlimit="10"
      ></rect>{" "}
      <path
        d="M3,23a2.006,2.006,0,0,1-2-2V13H2"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>{" "}
      <path
        d="M5,1V21a2.006,2.006,0,0,1-2,2H21a2.006,2.006,0,0,0,2-2V1Z"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>
    </g>
  </svg>
)

// Gift icon for Wrapped
const WrappedIcon = [
  <svg
    key="wrapped-outline"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>wrapped</title>
    <g
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      fill="none"
      stroke="currentColor"
    >
      <rect x="3" y="10" width="18" height="12" rx="1" />
      <rect x="2" y="6" width="20" height="4" rx="1" />
      <line x1="12" y1="6" x2="12" y2="22" />
      <path d="M12 6C12 6 9 3 6 4C3 5 4 6 6 6C8 6 12 6 12 6Z" />
      <path d="M12 6C12 6 15 3 18 4C21 5 20 6 18 6C16 6 12 6 12 6Z" />
    </g>
  </svg>,
  <svg
    key="wrapped-filled"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>wrapped</title>
    <g fill="currentColor">
      <path d="M3 11H11V22H4C3.45 22 3 21.55 3 21V11Z" />
      <path d="M13 11H21V21C21 21.55 20.55 22 20 22H13V11Z" />
      <path d="M2 7C2 6.45 2.45 6 3 6H11V10H2V7Z" />
      <path d="M13 6H21C21.55 6 22 6.45 22 7V10H13V6Z" />
      <path d="M12 6C12 6 9 2 5.5 3.5C2 5 4 6 6.5 6C9 6 12 6 12 6Z" />
      <path d="M12 6C12 6 15 2 18.5 3.5C22 5 20 6 17.5 6C15 6 12 6 12 6Z" />
      <circle cx="12" cy="5" r="1.5" />
    </g>
  </svg>,
]

const SettingsIcon = [
  <svg
    key="0s-ddasdf"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>settings-gear</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <circle data-color="color-2" cx="12" cy="12" r="3" fill="none" strokeMiterlimit="10"></circle>{" "}
      <path
        d="M20,12a8.049,8.049,0,0,0-.188-1.713l2.714-2.055-2-3.464L17.383,6.094a7.987,7.987,0,0,0-2.961-1.719L14,1H10L9.578,4.375A7.987,7.987,0,0,0,6.617,6.094L3.474,4.768l-2,3.464,2.714,2.055a7.9,7.9,0,0,0,0,3.426L1.474,15.768l2,3.464,3.143-1.326a7.987,7.987,0,0,0,2.961,1.719L10,23h4l.422-3.375a7.987,7.987,0,0,0,2.961-1.719l3.143,1.326,2-3.464-2.714-2.055A8.049,8.049,0,0,0,20,12Z"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>
    </g>
  </svg>,
  <svg
    key="0s-aasdf"
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>settings-gear</title>
    <g fill="currentColor">
      <path
        d="M20.872,13.453A9.033,9.033,0,0,0,21,12a9.033,9.033,0,0,0-.128-1.453l2.1-2.029A1,1,0,0,0,23.143,7.3l-1.5-2.6A1.009,1.009,0,0,0,20.5,4.239l-2.8.8a9.017,9.017,0,0,0-2.527-1.451L14.47.758A1,1,0,0,0,13.5,0h-3a1,1,0,0,0-.97.758l-.707,2.83A9.017,9.017,0,0,0,6.3,5.039l-2.8-.8A1.01,1.01,0,0,0,2.357,4.7L.857,7.3a1,1,0,0,0,.171,1.219l2.1,2.029A9.033,9.033,0,0,0,3,12a9.033,9.033,0,0,0,.128,1.453l-2.1,2.029A1,1,0,0,0,.857,16.7l1.5,2.6a1,1,0,0,0,.867.5,1.048,1.048,0,0,0,.275-.038l2.8-.8a9.017,9.017,0,0,0,2.527,1.451l.707,2.83A1,1,0,0,0,10.5,24h3a1,1,0,0,0,.97-.758l.707-2.83A9.017,9.017,0,0,0,17.7,18.961l2.8.8a1.048,1.048,0,0,0,.275.038,1,1,0,0,0,.867-.5l1.5-2.6a1,1,0,0,0-.171-1.219ZM12,16a4,4,0,1,1,4-4A4,4,0,0,1,12,16Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>,
]

const AdminIcon = [
  <svg
    key="aiasdflkssdfajh222"
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
  >
    <title>pickaxe</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <polyline
        points="18.899 7.929 21.914 4.914 19.086 2.086 16.073 5.099"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="butt"
      ></polyline>
      <path
        data-color="color-2"
        d="M21,16,8,3,9.637,1.363A22.052,22.052,0,0,1,22.626,14.374Z"
        fill="none"
        strokeMiterlimit="10"
      ></path>
      <path
        d="M10.257,10.914,1.586,19.586a2,2,0,0,0,2.828,2.828l8.672-8.671"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></path>
    </g>
  </svg>,
  <svg
    key="asdlkjh22098a7slkj"
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
  >
    <title>pickaxe</title>
    <g fill="currentColor">
      <path
        data-color="color-2"
        d="M9.965,11.207.586,20.586a2,2,0,0,0,2.828,2.828l9.379-9.379Z"
      ></path>
      <path
        d="M23.5,17.118A26.9,26.9,0,0,0,18.43,8.4l1-1A2,2,0,0,0,16.6,4.571l-1,1A26.923,26.923,0,0,0,6.882.5,1,1,0,0,0,5.846.74L4.793,1.793a1,1,0,0,0,0,1.414l16,16a1,1,0,0,0,1.414,0l1.053-1.053A1,1,0,0,0,23.5,17.118Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>,
]

const TemplatesIcon = [
  <svg
    key="aiasdflkjh222"
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
  >
    <title>spaceship</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <line
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        x1="14.935"
        y1="3.093"
        x2="20.882"
        y2="9.04"
        strokeLinecap="butt"
      ></line>{" "}
      <path
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        d="M12.603,4.387 C9.682,3.129,6.164,3.693,3.779,6.079c-0.489,0.489-0.893,1.03-1.229,1.6l3.428,3.428"
        strokeLinecap="butt"
      ></path>{" "}
      <path
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        d="M19.597,11.345 c1.29,2.932,0.725,6.475-1.676,8.876c-0.489,0.489-1.03,0.893-1.6,1.229l-3.442-3.442"
        strokeLinecap="butt"
      ></path>{" "}
      <path
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        d="M10.143,19.514 l-5.657-5.657c0,0,4.832-12.021,18.385-12.728C22.104,14.623,10.143,19.514,10.143,19.514z"
      ></path>{" "}
      <ellipse
        data-color="color-2"
        data-stroke="none"
        transform="matrix(0.7071 -0.7071 0.7071 0.7071 -2.9706 12.8284)"
        cx="14"
        cy="10"
        rx="2"
        ry="2"
        strokeLinejoin="miter"
        strokeLinecap="square"
        stroke="none"
      ></ellipse>{" "}
      <path
        data-color="color-2"
        data-stroke="none"
        d="M1.113,18.844c1.111-1.111,2.911-1.111,4.022,0c1.111,1.111,1.111,2.911,0,4.022 S0,24,0,24S0.002,19.954,1.113,18.844z"
        strokeLinejoin="miter"
        strokeLinecap="square"
        stroke="none"
      ></path>
    </g>
  </svg>,
  <svg
    key="aiasdssflkjh222"
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
  >
    <title>spaceship</title>
    <g fill="currentColor">
      <path
        d="M23.58.424A1,1,0,0,0,22.819.13C8.791.862,3.609,13.358,3.559,13.484a1,1,0,0,0,.22,1.08l5.657,5.657a1,1,0,0,0,1.085.218c.125-.051,12.554-5.291,13.348-19.253A1,1,0,0,0,23.58.424Zm-8.166,10.99a2,2,0,1,1,0-2.828A2,2,0,0,1,15.414,11.414Z"
        fill="currentColor"
      ></path>{" "}
      <path
        data-color="color-2"
        d="M1.113,18.844a2.844,2.844,0,1,1,4.022,4.022C4.024,23.977,0,24,0,24S0,19.954,1.113,18.844Z"
      ></path>{" "}
      <path
        id="color-2"
        d="M10.357,2.341A8.911,8.911,0,0,0,2.522,4.825a9.084,9.084,0,0,0-1.384,1.8,1,1,0,0,0,.155,1.215l1.989,1.99A26.623,26.623,0,0,1,10.357,2.341Z"
        fill="currentColor"
      ></path>{" "}
      <path
        id="color-3"
        d="M21.659,13.643a8.911,8.911,0,0,1-2.484,7.835,9.084,9.084,0,0,1-1.8,1.384,1,1,0,0,1-1.215-.155l-1.99-1.989A26.623,26.623,0,0,0,21.659,13.643Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>,
]

const SelectOrganizationIcon = [
  <svg
    key="asldkjfh2222a"
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
  >
    <title>new-construction</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <polyline
        points="5 23 1 23 1 13 5 13"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></polyline>
      <line
        x1="9"
        y1="16"
        x2="11"
        y2="16"
        fill="none"
        strokeMiterlimit="10"
        data-color="color-2"
      ></line>
      <line
        x1="9"
        y1="19"
        x2="11"
        y2="19"
        fill="none"
        strokeMiterlimit="10"
        data-color="color-2"
      ></line>
      <line
        x1="9"
        y1="13"
        x2="11"
        y2="13"
        fill="none"
        strokeMiterlimit="10"
        data-color="color-2"
      ></line>
      <polyline
        points="13 6 13 2 23 5 23 23 5 23 5 9 15 9 15 23"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      ></polyline>
      <line
        x1="19"
        y1="19"
        x2="19"
        y2="9"
        fill="none"
        strokeMiterlimit="10"
        data-color="color-2"
      ></line>
    </g>
  </svg>,
  <svg
    key="asldkjfh2222a"
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
  >
    <title>new-construction</title>
    <g fill="currentColor">
      <path
        d="M23.287,3.042l-10-3A1,1,0,0,0,12,1V6h2V2.344l8,2.4V22H16V9a1,1,0,0,0-1-1H5A1,1,0,0,0,4,9V22H2V12H1a1,1,0,0,0-1,1V23a1,1,0,0,0,1,1H23a1,1,0,0,0,1-1V4A1,1,0,0,0,23.287,3.042ZM11.5,21h-3a.5.5,0,0,1-.5-.5v-1a.5.5,0,0,1,.5-.5h3a.5.5,0,0,1,.5.5v1A.5.5,0,0,1,11.5,21Zm0-4h-3a.5.5,0,0,1-.5-.5v-1a.5.5,0,0,1,.5-.5h3a.5.5,0,0,1,.5.5v1A.5.5,0,0,1,11.5,17Zm0-4h-3a.5.5,0,0,1-.5-.5v-1a.5.5,0,0,1,.5-.5h3a.5.5,0,0,1,.5.5v1A.5.5,0,0,1,11.5,13Z"
        fill="currentColor"
      ></path>
      <rect data-color="color-2" x="18" y="8" width="2" height="12" rx="0.5"></rect>
    </g>
  </svg>,
]

const LogoSmall = (
  <svg width="32" height="32" viewBox="0 0 46 43" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="46" height="43" />
    <path
      d="M10.9399 36.7987C11.1706 36.7987 11.354 36.7316 11.4903 36.5975C11.6267 36.4634 11.6948 36.2828 11.6948 36.0559V33.8893C13.9176 33.6623 15.6844 32.9917 16.995 31.8774C18.3057 30.7632 18.961 29.3084 18.961 27.5133C18.961 26.2958 18.6989 25.2951 18.1746 24.511C17.6503 23.7269 16.8377 23.0769 15.7368 22.561C14.6359 22.0452 13.1522 21.5809 11.2859 21.1682C9.94379 20.8381 8.91101 20.5285 8.18754 20.2397C7.46407 19.9508 6.93981 19.6206 6.61477 19.2492C6.28974 18.8778 6.12722 18.4135 6.12722 17.8564C6.12722 17.031 6.4575 16.4017 7.11806 15.9684C7.77862 15.535 8.68558 15.3184 9.83894 15.3184C10.8875 15.3184 11.763 15.5402 12.4655 15.9838C13.168 16.4275 13.5821 16.9794 13.7079 17.6397C13.8757 18.0112 14.1798 18.1969 14.6201 18.1969H17.6084C17.7971 18.1969 17.9544 18.135 18.0802 18.0112C18.2061 17.8874 18.269 17.7326 18.269 17.5469C18.227 16.7834 17.9492 15.9993 17.4354 15.1946C16.9216 14.3898 16.1772 13.6831 15.2021 13.0744C14.227 12.4657 13.0579 12.0685 11.6948 11.8828V9.68522C11.6948 9.45824 11.6267 9.27769 11.4903 9.14357C11.354 9.00944 11.1706 8.94238 10.9399 8.94238H8.98965C8.77995 8.94238 8.6017 9.00944 8.45491 9.14357C8.30812 9.27769 8.23472 9.45824 8.23472 9.68522V11.8209C6.17964 12.0685 4.55969 12.7494 3.37488 13.8637C2.19006 14.9779 1.59765 16.3398 1.59765 17.9493C1.59765 19.7238 2.17433 21.0857 3.32769 22.0348C4.48105 22.984 6.29498 23.7475 8.76946 24.3253C10.2583 24.7173 11.3855 25.0526 12.1509 25.3312C12.9163 25.6097 13.4878 25.9347 13.8652 26.3061C14.2427 26.6776 14.4314 27.1418 14.4314 27.699C14.4314 28.545 14.0592 29.2104 13.3148 29.6953C12.5703 30.1802 11.4746 30.4227 10.0277 30.4227C8.72752 30.4227 7.70522 30.1854 6.96078 29.7108C6.21634 29.2362 5.72878 28.6481 5.49811 27.9466C5.37229 27.7402 5.24122 27.5906 5.10492 27.4978C4.96861 27.4049 4.77464 27.3585 4.52299 27.3585H1.69202C1.50328 27.3585 1.34077 27.4256 1.20446 27.5597C1.06815 27.6938 1 27.8434 1 28.0085C1.04194 28.9577 1.34077 29.8501 1.89648 30.6858C2.45219 31.5215 3.27003 32.2282 4.34999 32.806C5.42996 33.3837 6.72487 33.7448 8.23472 33.8893V36.0559C8.23472 36.2828 8.30812 36.4634 8.45491 36.5975C8.6017 36.7316 8.77995 36.7987 8.98965 36.7987H10.9399Z"
      fill="#74FFF1"
      stroke="#74FFF1"
      strokeWidth="0.5"
    />
    <path
      d="M28.7343 29.666C29.3005 29.666 29.6674 29.4081 29.8352 28.8922L33.3582 18.6163L36.8812 28.8922C36.9441 29.0986 37.0699 29.2791 37.2586 29.4339C37.4474 29.5886 37.6885 29.666 37.9821 29.666H40.1211C40.4356 29.666 40.6925 29.5835 40.8917 29.4184C41.091 29.2533 41.2115 29.0264 41.2535 28.7375L44.9652 8.89759C44.9862 8.83569 44.9967 8.75315 44.9967 8.64998C44.9967 8.46427 44.9337 8.30951 44.8079 8.18571C44.6821 8.0619 44.5353 8 44.3676 8H41.5366C41.0123 8 40.7187 8.20634 40.6558 8.61903L38.0765 22.5472L35.2455 13.5713C35.1826 13.3649 35.0673 13.1844 34.8995 13.0296C34.7317 12.8749 34.5116 12.7975 34.2389 12.7975H32.5089C32.0266 12.7975 31.6806 13.0554 31.4709 13.5713L28.6713 22.5472L26.0606 8.61903C26.0186 8.39205 25.9347 8.23214 25.8089 8.13928C25.6831 8.04643 25.4839 8 25.2113 8H22.3803C22.1916 8 22.0343 8.0619 21.9085 8.18571C21.7826 8.30951 21.7197 8.46427 21.7197 8.64998L21.7512 8.89759L25.4629 28.7375C25.6097 29.3565 25.9872 29.666 26.5953 29.666H28.7343Z"
      fill="white"
      stroke="white"
      strokeWidth="0.5"
    />
  </svg>
)

const LogoLarge = (
  <svg
    width="150px"
    height="18px"
    viewBox="0 0 157 22"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Logo</title>
    <g id="Design-Guidelines" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g
        id="Superwall-Design/Guidelines-Copy"
        transform="translate(-139.000000, -71.000000)"
        fillRule="nonzero"
        strokeWidth="0.5"
      >
        <g id="Group" transform="translate(139.579010, 71.340393)">
          <path
            d="M7.268,20.7 C7.43666667,20.7 7.57083333,20.6501667 7.6705,20.5505 C7.77016667,20.4508333 7.82,20.3166667 7.82,20.148 L7.82,20.148 L7.82,18.538 C9.44533333,18.3693333 10.7371667,17.871 11.6955,17.043 C12.6538333,16.215 13.133,15.134 13.133,13.8 C13.133,12.8953333 12.9413333,12.1516667 12.558,11.569 C12.1746667,10.9863333 11.5805,10.5033333 10.7755,10.12 C9.9705,9.73666667 8.88566667,9.39166667 7.521,9.085 C6.53966667,8.83966667 5.7845,8.60966667 5.2555,8.395 C4.7265,8.18033333 4.34316667,7.935 4.1055,7.659 C3.86783333,7.383 3.749,7.038 3.749,6.624 C3.749,6.01066667 3.9905,5.543 4.4735,5.221 C4.9565,4.899 5.61966667,4.738 6.463,4.738 C7.22966667,4.738 7.86983333,4.90283333 8.3835,5.2325 C8.89716667,5.56216667 9.2,5.97233333 9.292,6.463 C9.41466667,6.739 9.637,6.877 9.959,6.877 L9.959,6.877 L12.144,6.877 C12.282,6.877 12.397,6.831 12.489,6.739 C12.581,6.647 12.627,6.532 12.627,6.394 C12.5963333,5.82666667 12.3931667,5.244 12.0175,4.646 C11.6418333,4.048 11.0975,3.52283333 10.3845,3.0705 C9.6715,2.61816667 8.81666667,2.323 7.82,2.185 L7.82,2.185 L7.82,0.552 C7.82,0.383333333 7.77016667,0.249166667 7.6705,0.1495 C7.57083333,0.0498333333 7.43666667,-3.55271368e-14 7.268,-3.55271368e-14 L7.268,-3.55271368e-14 L5.842,-3.55271368e-14 C5.68866667,-3.55271368e-14 5.55833333,0.0498333333 5.451,0.1495 C5.34366667,0.249166667 5.29,0.383333333 5.29,0.552 L5.29,0.552 L5.29,2.139 C3.78733333,2.323 2.60283333,2.829 1.7365,3.657 C0.870166667,4.485 0.437,5.497 0.437,6.693 C0.437,8.01166667 0.858666667,9.02366667 1.702,9.729 C2.54533333,10.4343333 3.87166667,11.0016667 5.681,11.431 C6.76966667,11.7223333 7.59383333,11.9715 8.1535,12.1785 C8.71316667,12.3855 9.131,12.627 9.407,12.903 C9.683,13.179 9.821,13.524 9.821,13.938 C9.821,14.5666667 9.54883333,15.0611667 9.0045,15.4215 C8.46016667,15.7818333 7.659,15.962 6.601,15.962 C5.65033333,15.962 4.90283333,15.7856667 4.3585,15.433 C3.81416667,15.0803333 3.45766667,14.6433333 3.289,14.122 C3.197,13.9686667 3.10116667,13.8575 3.0015,13.7885 C2.90183333,13.7195 2.76,13.685 2.576,13.685 L2.576,13.685 L0.506,13.685 C0.368,13.685 0.249166667,13.7348333 0.1495,13.8345 C0.0498333333,13.9341667 -1.42108547e-14,14.0453333 -1.42108547e-14,14.168 C0.0306666667,14.8733333 0.249166667,15.5365 0.6555,16.1575 C1.06183333,16.7785 1.65983333,17.3036667 2.4495,17.733 C3.23916667,18.1623333 4.186,18.4306667 5.29,18.538 L5.29,18.538 L5.29,20.148 C5.29,20.3166667 5.34366667,20.4508333 5.451,20.5505 C5.55833333,20.6501667 5.68866667,20.7 5.842,20.7 L5.842,20.7 L7.268,20.7 Z"
            id="Path"
            stroke="#74FFF1"
            fill="#74FFF1"
          ></path>
          <path
            d="M24.718,18.63 C26.788,18.63 28.3941667,18.101 29.5365,17.043 C30.6788333,15.985 31.25,14.3826667 31.25,12.236 L31.25,12.236 L31.25,2.875 C31.25,2.70633333 31.2001667,2.56833333 31.1005,2.461 C31.0008333,2.35366667 30.8666667,2.3 30.698,2.3 L30.698,2.3 L28.582,2.3 C28.4133333,2.3 28.2753333,2.35366667 28.168,2.461 C28.0606667,2.56833333 28.007,2.70633333 28.007,2.875 L28.007,2.875 L28.007,12.236 C28.007,13.4473333 27.7195,14.3596667 27.1445,14.973 C26.5695,15.5863333 25.753,15.893 24.695,15.893 C23.637,15.893 22.8205,15.5863333 22.2455,14.973 C21.6705,14.3596667 21.383,13.4473333 21.383,12.236 L21.383,12.236 L21.383,2.875 C21.383,2.70633333 21.3293333,2.56833333 21.222,2.461 C21.1146667,2.35366667 20.9766667,2.3 20.808,2.3 L20.808,2.3 L18.692,2.3 C18.5233333,2.3 18.3891667,2.35366667 18.2895,2.461 C18.1898333,2.56833333 18.14,2.70633333 18.14,2.875 L18.14,2.875 L18.14,12.236 C18.14,14.3826667 18.715,15.985 19.865,17.043 C21.015,18.101 22.6326667,18.63 24.718,18.63 Z"
            id="Path"
            stroke="#74FFF1"
            fill="#74FFF1"
          ></path>
          <path
            d="M43.916,2.3 L44.2592031,2.30512109 C45.9518594,2.35633203 47.294125,2.791625 48.286,3.611 C49.344,4.485 49.873,5.75766667 49.873,7.429 C49.873,9.085 49.344,10.3385 48.286,11.1895 C47.228,12.0405 45.7713333,12.466 43.916,12.466 L43.916,12.466 L40.535,12.466 L40.535,17.848 L40.52856,17.94506 C40.5113867,18.0689533 40.4598667,18.1707667 40.374,18.2505 C40.2666667,18.3501667 40.1286667,18.4 39.96,18.4 L39.96,18.4 L37.752,18.4 L37.66276,18.39356 C37.5474533,18.3763867 37.4468667,18.3248667 37.361,18.239 C37.2536667,18.1316667 37.2,18.0013333 37.2,17.848 L37.2,17.848 L37.2,2.875 L37.20598,2.77748 C37.2219267,2.65236 37.2697667,2.54686667 37.3495,2.461 C37.4491667,2.35366667 37.5833333,2.3 37.752,2.3 L37.752,2.3 L43.916,2.3 Z M43.801,4.876 L40.489,4.876 L40.489,9.89 L43.801,9.89 C44.6903333,9.89 45.3688333,9.67916667 45.8365,9.2575 C46.3041667,8.83583333 46.538,8.21866667 46.538,7.406 C46.538,6.60866667 46.3118333,5.98766667 45.8595,5.543 C45.4071667,5.09833333 44.721,4.876 43.801,4.876 L43.801,4.876 Z"
            id="Combined-Shape"
            stroke="#74FFF1"
            fill="#74FFF1"
          ></path>
          <path
            d="M65.759,18.4 C65.9276667,18.4 66.0656667,18.3501667 66.173,18.2505 C66.2803333,18.1508333 66.334,18.0166667 66.334,17.848 L66.334,17.848 L66.334,16.284 C66.334,16.1153333 66.2803333,15.9811667 66.173,15.8815 C66.0656667,15.7818333 65.9276667,15.732 65.759,15.732 L65.759,15.732 L58.031,15.732 L58.031,11.569 L65.069,11.569 C65.2376667,11.569 65.3756667,11.5191667 65.483,11.4195 C65.5903333,11.3198333 65.644,11.1856667 65.644,11.017 L65.644,11.017 L65.644,9.568 C65.644,9.39933333 65.5903333,9.26133333 65.483,9.154 C65.3756667,9.04666667 65.2376667,8.993 65.069,8.993 L65.069,8.993 L58.031,8.993 L58.031,4.968 L65.575,4.968 C65.7436667,4.968 65.8816667,4.91816667 65.989,4.8185 C66.0963333,4.71883333 66.15,4.58466667 66.15,4.416 L66.15,4.416 L66.15,2.875 C66.15,2.70633333 66.0963333,2.56833333 65.989,2.461 C65.8816667,2.35366667 65.7436667,2.3 65.575,2.3 L65.575,2.3 L55.432,2.3 C55.2633333,2.3 55.1291667,2.35366667 55.0295,2.461 C54.9298333,2.56833333 54.88,2.70633333 54.88,2.875 L54.88,2.875 L54.88,17.848 C54.88,18.0013333 54.9336667,18.1316667 55.041,18.239 C55.1483333,18.3463333 55.2786667,18.4 55.432,18.4 L55.432,18.4 L65.759,18.4 Z"
            id="Path"
            stroke="#74FFF1"
            fill="#74FFF1"
          ></path>
          <path
            d="M78.218,2.3 L78.5668184,2.30512109 C80.286877,2.35633203 81.6464375,2.791625 82.6455,3.611 C83.7111667,4.485 84.244,5.727 84.244,7.337 C84.244,8.45633333 83.9756667,9.3955 83.439,10.1545 C82.9023333,10.9135 82.151,11.4616667 81.185,11.799 L81.185,11.799 L84.52,17.664 L84.5583333,17.7534444 C84.5787778,17.8113704 84.589,17.8658889 84.589,17.917 C84.589,18.055 84.5391667,18.17 84.4395,18.262 C84.3398333,18.354 84.2286667,18.4 84.106,18.4 L84.106,18.4 L81.99,18.4 L81.84924,18.3931 C81.6701467,18.3747 81.5254,18.3195 81.415,18.2275 C81.277,18.1125 81.1543333,17.963 81.047,17.779 L81.047,17.779 L78.103,12.328 L74.952,12.328 L74.952,17.848 L74.94556,17.93724 C74.9283867,18.0525467 74.8768667,18.1531333 74.791,18.239 C74.6836667,18.3463333 74.5456667,18.4 74.377,18.4 L74.377,18.4 L72.261,18.4 L72.17176,18.39356 C72.0564533,18.3763867 71.9558667,18.3248667 71.87,18.239 C71.7626667,18.1316667 71.709,18.0013333 71.709,17.848 L71.709,17.848 L71.709,2.875 L71.71498,2.77748 C71.7309267,2.65236 71.7787667,2.54686667 71.8585,2.461 C71.9581667,2.35366667 72.0923333,2.3 72.261,2.3 L72.261,2.3 L78.218,2.3 Z M78.149,4.876 L74.952,4.876 L74.952,9.706 L78.149,9.706 C79.069,9.706 79.759,9.50283333 80.219,9.0965 C80.679,8.69016667 80.909,8.096 80.909,7.314 C80.909,6.532 80.679,5.93016667 80.219,5.5085 C79.759,5.08683333 79.069,4.876 78.149,4.876 L78.149,4.876 Z"
            id="Combined-Shape"
            stroke="#74FFF1"
            fill="#74FFF1"
          ></path>
          <path
            d="M93.713,18.4 C94.127,18.4 94.3953333,18.2083333 94.518,17.825 L94.518,17.825 L97.094,10.189 L99.67,17.825 C99.716,17.9783333 99.808,18.1125 99.946,18.2275 C100.084,18.3425 100.260333,18.4 100.475,18.4 L100.475,18.4 L102.039,18.4 C102.269,18.4 102.456833,18.3386667 102.6025,18.216 C102.748167,18.0933333 102.836333,17.9246667 102.867,17.71 L102.867,17.71 L105.581,2.967 C105.596333,2.921 105.604,2.85966667 105.604,2.783 C105.604,2.645 105.558,2.53 105.466,2.438 C105.374,2.346 105.266667,2.3 105.144,2.3 L105.144,2.3 L103.074,2.3 C102.690667,2.3 102.476,2.45333333 102.43,2.76 L102.43,2.76 L100.544,13.11 L98.474,6.44 C98.428,6.28666667 98.3436667,6.1525 98.221,6.0375 C98.0983333,5.9225 97.9373333,5.865 97.738,5.865 L97.738,5.865 L96.473,5.865 C96.1203333,5.865 95.8673333,6.05666667 95.714,6.44 L95.714,6.44 L93.667,13.11 L91.758,2.76 C91.7273333,2.59133333 91.666,2.4725 91.574,2.4035 C91.482,2.3345 91.3363333,2.3 91.137,2.3 L91.137,2.3 L89.067,2.3 C88.929,2.3 88.814,2.346 88.722,2.438 C88.63,2.53 88.584,2.645 88.584,2.783 L88.584,2.783 L88.607,2.967 L91.321,17.71 C91.4283333,18.17 91.7043333,18.4 92.149,18.4 L92.149,18.4 L93.713,18.4 Z"
            id="Path"
            stroke="#FFFFFF"
            fill="#FFFFFF"
          ></path>
          <path
            d="M117.58,2.3 L117.708346,2.30709877 C118.038486,2.34495885 118.271704,2.53425926 118.408,2.875 L118.408,2.875 L123.928,17.71 L123.974,17.917 L123.96802,17.99704 C123.952073,18.10008 123.904233,18.1884 123.8245,18.262 C123.724833,18.354 123.613667,18.4 123.491,18.4 L123.491,18.4 L121.444,18.4 L121.327922,18.3931719 C121.067974,18.3613073 120.884333,18.2179167 120.777,17.963 L120.777,17.963 L119.719,15.18 L112.911,15.18 L111.853,17.963 L111.802328,18.0654219 C111.673193,18.288474 111.46775,18.4 111.186,18.4 L111.186,18.4 L109.139,18.4 L109.039813,18.391375 C108.944938,18.374125 108.863,18.331 108.794,18.262 C108.702,18.17 108.656,18.055 108.656,17.917 L108.656,17.917 L108.702,17.71 L114.222,2.875 L114.277654,2.75432099 C114.438181,2.45144033 114.69563,2.3 115.05,2.3 L115.05,2.3 L117.58,2.3 Z M116.315,5.428 L113.716,12.512 L118.914,12.512 L116.315,5.428 Z"
            id="Combined-Shape"
            stroke="#FFFFFF"
            fill="#FFFFFF"
          ></path>
          <path
            d="M139.147,18.4 C139.315667,18.4 139.453667,18.3463333 139.561,18.239 C139.668333,18.1316667 139.722,18.0013333 139.722,17.848 L139.722,17.848 L139.722,16.192 C139.722,16.0386667 139.668333,15.9083333 139.561,15.801 C139.453667,15.6936667 139.315667,15.64 139.147,15.64 L139.147,15.64 L131.856,15.64 L131.856,2.852 C131.856,2.68333333 131.806167,2.54916667 131.7065,2.4495 C131.606833,2.34983333 131.472667,2.3 131.304,2.3 L131.304,2.3 L129.142,2.3 C128.988667,2.3 128.858333,2.34983333 128.751,2.4495 C128.643667,2.54916667 128.59,2.68333333 128.59,2.852 L128.59,2.852 L128.59,17.848 C128.59,18.0013333 128.643667,18.1316667 128.751,18.239 C128.858333,18.3463333 128.988667,18.4 129.142,18.4 L129.142,18.4 L139.147,18.4 Z"
            id="Path"
            stroke="#FFFFFF"
            fill="#FFFFFF"
          ></path>
          <path
            d="M155.01,18.4 C155.178667,18.4 155.316667,18.3463333 155.424,18.239 C155.531333,18.1316667 155.585,18.0013333 155.585,17.848 L155.585,17.848 L155.585,16.192 C155.585,16.0386667 155.531333,15.9083333 155.424,15.801 C155.316667,15.6936667 155.178667,15.64 155.01,15.64 L155.01,15.64 L147.719,15.64 L147.719,2.852 C147.719,2.68333333 147.669167,2.54916667 147.5695,2.4495 C147.469833,2.34983333 147.335667,2.3 147.167,2.3 L147.167,2.3 L145.005,2.3 C144.851667,2.3 144.721333,2.34983333 144.614,2.4495 C144.506667,2.54916667 144.453,2.68333333 144.453,2.852 L144.453,2.852 L144.453,17.848 C144.453,18.0013333 144.506667,18.1316667 144.614,18.239 C144.721333,18.3463333 144.851667,18.4 145.005,18.4 L145.005,18.4 L155.01,18.4 Z"
            id="Path"
            stroke="#FFFFFF"
            fill="#FFFFFF"
          ></path>
        </g>
      </g>
    </g>
  </svg>
)

const MenuIcons = [
  <svg
    key="asdfass9992hk"
    xmlns="http://www.w3.org/2000/svg"
    width={22}
    height={22}
    viewBox="0 0 24 24"
  >
    <title>menu</title>
    <g fill="currentColor">
      <path
        data-color="color-2"
        d="M23,13H1c-0.552,0-1-0.447-1-1s0.448-1,1-1h22c0.552,0,1,0.447,1,1S23.552,13,23,13z"
      ></path>{" "}
      <path
        fill="currentColor"
        d="M23,6H12c-0.552,0-1-0.447-1-1s0.448-1,1-1h11c0.552,0,1,0.447,1,1S23.552,6,23,6z"
      ></path>{" "}
      <path
        fill="currentColor"
        d="M12,20H1c-0.552,0-1-0.447-1-1s0.448-1,1-1h11c0.552,0,1,0.447,1,1S12.552,20,12,20z"
      ></path>
    </g>
  </svg>,
  <svg
    key="asd872340982bn"
    xmlns="http://www.w3.org/2000/svg"
    width={22}
    height={22}
    viewBox="0 0 24 24"
  >
    <title>e-remove</title>
    <g fill="currentColor">
      <path
        fill="currentColor"
        d="M19.7,4.3c-0.4-0.4-1-0.4-1.4,0L12,10.6L5.7,4.3c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4l6.3,6.3l-6.3,6.3 c-0.4,0.4-0.4,1,0,1.4C4.5,19.9,4.7,20,5,20s0.5-0.1,0.7-0.3l6.3-6.3l6.3,6.3c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3 c0.4-0.4,0.4-1,0-1.4L13.4,12l6.3-6.3C20.1,5.3,20.1,4.7,19.7,4.3z"
      ></path>
    </g>
  </svg>,
]

const QuickstartIcon = [
  <svg
    key={"qs-1"}
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>lightning</title>
    <g
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <polygon
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        points="13,3 3,14 12,14 11,21 21,10 12,10 "
      ></polygon>
    </g>
  </svg>,
  <svg
    key={"qs-2"}
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <title>lightning</title>
    <g fill="currentColor">
      <path
        fill="currentColor"
        d="M21.897,9.558C21.729,9.216,21.381,9,21,9h-7.883l0.876-7.89c0.05-0.446-0.204-0.87-0.62-1.038 c-0.417-0.17-0.894-0.038-1.167,0.317l-10,13c-0.232,0.302-0.273,0.71-0.104,1.052C2.271,14.784,2.619,15,3,15h7.883l-0.876,7.89 c-0.05,0.446,0.204,0.87,0.62,1.038C10.748,23.977,10.875,24,11,24c0.304,0,0.6-0.139,0.793-0.39l10-13 C22.025,10.308,22.065,9.9,21.897,9.558z"
      ></path>
    </g>
  </svg>,
]

const SurveysIcon = [
  <svg
    key={`survey1`}
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <g
      className="nc-icon-wrapper"
      strokeLinecap="square"
      strokeLinejoin="miter"
      strokeWidth="2"
      fill="currentColor"
      stroke="currentColor"
    >
      <line x1="12" y1="6" x2="23" y2="6" fill="none" stroke="currentColor" strokeMiterlimit="10" />{" "}
      <rect
        x="2"
        y="3"
        width="6"
        height="6"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
      />{" "}
      <line
        x1="12"
        y1="18"
        x2="23"
        y2="18"
        fill="none"
        strokeMiterlimit="10"
        data-color="color-2"
      />{" "}
      <polyline points="2 18 4 20 10 14" fill="none" strokeMiterlimit="10" data-color="color-2" />
    </g>
  </svg>,

  <svg
    key={`survey2`}
    xmlns="http://www.w3.org/2000/svg"
    width={KIconWidth}
    height={KIconWidth}
    viewBox="0 0 24 24"
  >
    <g className="nc-icon-wrapper" fill="currentColor">
      <path
        d="M23,5H12c-.552,0-1,.448-1,1s.448,1,1,1h11c.552,0,1-.448,1-1s-.448-1-1-1Z"
        fill="currentColor"
      />
      <rect x="1" y="2" width="8" height="8" rx="1" ry="1" fill="currentColor" />
      <path
        data-color="color-2"
        d="M23,17H12c-.552,0-1,.448-1,1s.448,1,1,1h11c.552,0,1-.448,1-1s-.448-1-1-1Z"
      />
      <path
        data-color="color-2"
        d="M4,21c-.256,0-.512-.098-.707-.293l-2-2c-.391-.391-.391-1.023,0-1.414s1.023-.391,1.414,0l1.293,1.293,5.293-5.293c.391-.391,1.023-.391,1.414,0s.391,1.023,0,1.414l-6,6c-.195,.195-.451,.293-.707,.293Z"
      />
    </g>
  </svg>,
]

export default Sidebar
